import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Alert } from 'react-native';
import DocumentPicker, { DocumentPickerResponse } from 'react-native-document-picker';
import { launchImageLibrary, launchCamera, ImagePickerResponse } from 'react-native-image-picker';
import { FormSection, FormSectionThemeColors } from '@/components/common/admin/mobile/FormSection';
import { SVG_COMPONENTS } from '@/assets/admin';
import { SUPPORTED_IMAGE_MIME_TYPES, isValidImageMimeType, SUPPORTED_FORMATS_DISPLAY } from '@/constants/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { showAlert, showConfirmWithButtons } from '@/utils/common/alert';
import { logger } from '@/utils/common/logger';

export interface UploadFile {
  id: string;
  name: string;
  uri: string;
  type: string;
  size?: number;
  file?: any; // For FormData
}

export interface FileUploadThemeColors {
  surface: string;
  border: string;
  primary: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  iconBg: string;
  error: string;
}

interface FileUploadSectionProps {
  title: string;
  subtitle: string;
  files: UploadFile[];
  onFilesChange: (files: UploadFile[]) => void;
  error?: string;
  maxFiles?: number;
  allowedTypes?: string[];
  onAppendFiles?: (newFiles: UploadFile[]) => void; // Optional for edit mode
  themeColors?: FileUploadThemeColors;
}

/**
 * Hook resolving the default theme palette from admin tokens. Callers can
 * still pass an explicit `themeColors` prop to override the defaults.
 */
function useDefaultFileUploadColors(): FileUploadThemeColors {
  const { admin, redesign, semantic } = useAdminColors();
  return {
    surface: admin.dataTable.bg,
    border: admin.dataTable.border,
    primary: admin.formInput.borderFocused,
    textPrimary: admin.dataTable.text,
    textSecondary: redesign.text.dim,
    textTertiary: redesign.text.muted,
    iconBg: admin.dataTable.headerBg,
    error: semantic.status.error,
  };
}

export const FileUploadSection = memo<FileUploadSectionProps>(({
  title,
  subtitle,
  files,
  onFilesChange,
  error,
  maxFiles = 10,
  allowedTypes,
  onAppendFiles,
  themeColors,
}) => {
  const defaults = useDefaultFileUploadColors();
  const c = themeColors ?? defaults;
  const createdUrlsRef = useRef<Set<string>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  // Note: We intentionally do NOT revoke object URLs on unmount because
  // the parent component may still be using them for image previews.
  // URLs are only revoked when files are explicitly removed by the user.

  // Define handleImageResponse first since it's used by openCamera and openImageLibrary
  const handleImageResponse = useCallback((response: ImagePickerResponse) => {
    if (response.didCancel || response.errorMessage) {
      return;
    }

    if (response.assets) {
      const newFiles: UploadFile[] = response.assets.map((asset, index) => ({
        id: `${Date.now()}_${index}`,
        name: asset.fileName || `image_${Date.now()}_${index}.jpg`,
        uri: asset.uri || '',
        type: asset.type || 'image/jpeg',
        size: asset.fileSize || undefined,
        file: {
          uri: asset.uri || '',
          type: asset.type || 'image/jpeg',
          name: asset.fileName || `image_${Date.now()}_${index}.jpg`,
        },
      }));

      // For image-only mode, use specific MIME type validation
      let filteredFiles: UploadFile[];
      if (allowedTypes?.length === 1 && allowedTypes[0] === 'image') {
        filteredFiles = newFiles.filter(file => isValidImageMimeType(file.type));
        const invalidCount = newFiles.length - filteredFiles.length;
        if (invalidCount > 0) {
          showAlert(
            'Unsupported File Type',
            `${invalidCount} file(s) were not added. Please upload ${SUPPORTED_FORMATS_DISPLAY} images only.`
          );
        }
      } else {
        // Filter by allowed types if specified
        filteredFiles = allowedTypes
          ? newFiles.filter(file => allowedTypes.some(type => file.type.includes(type)))
          : newFiles;

        if (filteredFiles.length !== newFiles.length) {
          showAlert('Some Files Skipped', 'Some files were not added because they are not supported file types.');
        }
      }

      // Use onAppendFiles if available (edit mode), otherwise use onFilesChange (create mode)
      if (onAppendFiles) {
        onAppendFiles(filteredFiles);
      } else {
        const updatedFiles = [...files, ...filteredFiles].slice(0, maxFiles);
        onFilesChange(updatedFiles);
      }
    }
  }, [files, onFilesChange, maxFiles, allowedTypes, onAppendFiles]);

  const openCamera = useCallback(() => {
    const options = {
      mediaType: 'photo' as const,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
    };

    launchCamera(options, handleImageResponse);
  }, [handleImageResponse]);

  const openImageLibrary = useCallback(() => {
    const remainingSlots = maxFiles - files.length;
    const options = {
      mediaType: 'photo' as const,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
      selectionLimit: Math.min(remainingSlots, 5), // Allow multiple selection up to remaining slots
    };

    launchImageLibrary(options, handleImageResponse);
  }, [files.length, maxFiles, handleImageResponse]);

  const openDocumentPicker = useCallback(async () => {
    try {
      const remainingSlots = maxFiles - files.length;
      const results = await DocumentPicker.pick({
        type: allowedTypes ? allowedTypes.map(type => `*/${type}`) : [DocumentPicker.types.allFiles],
        allowMultiSelection: true,
        copyTo: 'documentDirectory', // Copy to app's document directory for persistence
      });

      // Limit to remaining slots
      const limitedResults = results.slice(0, remainingSlots);

      const newFiles: UploadFile[] = limitedResults.map((result, index) => ({
        id: `${Date.now()}_${index}`,
        name: result.name || `file_${Date.now()}_${index}`,
        uri: result.fileCopyUri || result.uri,
        type: result.type || 'application/octet-stream',
        size: result.size || undefined,
        file: {
          uri: result.fileCopyUri || result.uri,
          type: result.type || 'application/octet-stream',
          name: result.name || `file_${Date.now()}_${index}`,
        },
      }));

      if (limitedResults.length !== results.length) {
        showAlert('File Limit Reached', `Only ${limitedResults.length} files were added due to the ${maxFiles} file limit.`);
      }

      // Use onAppendFiles if available (edit mode), otherwise use onFilesChange (create mode)
      if (onAppendFiles) {
        onAppendFiles(newFiles);
      } else {
        onFilesChange([...files, ...newFiles]);
      }
    } catch (error) {
      if (DocumentPicker.isCancel(error)) {
        // User cancelled the picker
        return;
      }

      console.error('Document picker error:', error);
      showAlert('Error', 'Failed to select files. Please try again.');
    }
  }, [files, onFilesChange, maxFiles, allowedTypes, onAppendFiles]);

  // handleAddFiles must be defined after the functions it depends on
  const handleAddFiles = useCallback(() => {
    if (files.length >= maxFiles) {
      showAlert('Maximum Files Reached', `You can only upload up to ${maxFiles} files.`);
      return;
    }
    // On web, open a native file dialog directly
    if (Platform.OS === 'web') {
      openWebFilePicker(maxFiles - files.length, allowedTypes).then((picked) => {
        if (picked.length === 0) return;
        if (onAppendFiles) {
          onAppendFiles(picked);
        } else {
          const updated = [...files, ...picked].slice(0, maxFiles);
          onFilesChange(updated);
        }
      });
      return;
    }

    // For native, show action sheet with multiple options
    Alert.alert(
      'Add Files',
      'Choose how you want to add files',
      [
        {
          text: 'Photo Library',
          onPress: openImageLibrary,
        },
        {
          text: 'Take Photo',
          onPress: openCamera,
        },
        {
          text: 'Browse Files',
          onPress: openDocumentPicker,
        },
        {
          text: 'Cancel',
          style: 'cancel',
        },
      ],
      { cancelable: true }
    );
  }, [files.length, maxFiles, allowedTypes, onFilesChange, onAppendFiles, openImageLibrary, openCamera, openDocumentPicker]);

  // Check if allowedTypes is image-only mode (only contains "image")
  const isImageOnlyMode = (types?: string[]): boolean => {
    return types?.length === 1 && types[0] === 'image';
  };

  // Web-only file picker using input element
  const openWebFilePicker = async (limit: number, types?: string[]): Promise<UploadFile[]> => {
    return new Promise((resolve) => {
      const win: any = (globalThis as any)?.window;
      const doc: any = win?.document;
      if (!doc) {
        resolve([]);
        return;
      }
      const input = doc.createElement('input');
      input.type = 'file';
      input.multiple = limit > 1;

      // Use specific MIME types for image-only mode (matching admin events pattern)
      if (isImageOnlyMode(types)) {
        input.accept = SUPPORTED_IMAGE_MIME_TYPES.join(',');
      } else if (types && types.length > 0) {
        // Map common type keys to accept patterns
        const accept = types.map((t) => {
          if (t === 'image') return 'image/*';
          if (t === 'pdf') return 'application/pdf';
          if (t === 'document') return '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          if (t === 'spreadsheet') return '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          if (t === 'text') return 'text/plain';
          return '*/*';
        }).join(',');
        input.accept = accept;
      }
      input.onchange = () => {
        const list = Array.from((input.files || []) as any).slice(0, limit) as File[];

        // For image-only mode, validate file types and filter out unsupported ones
        let filesToProcess = list;
        if (isImageOnlyMode(types)) {
          const validFiles = list.filter((file: any) => isValidImageMimeType(file.type));
          const invalidCount = list.length - validFiles.length;

          if (invalidCount > 0) {
            showAlert(
              'Unsupported File Type',
              `${invalidCount} file(s) were not added. Please upload ${SUPPORTED_FORMATS_DISPLAY} images only.`
            );
          }

          filesToProcess = validFiles;
        }

        const uploads: UploadFile[] = filesToProcess.map((file: any, index: number) => {
          const uri = (win?.URL || URL).createObjectURL(file);
          // Track created object URLs for later revocation
          try {
            createdUrlsRef.current.add(uri);
          } catch (e) {
            logger.warn('fileUploadUrlTrackFailed', e);
          }
          return {
            id: `${Date.now()}_${index}`,
            name: file.name,
            uri,
            type: file.type || 'application/octet-stream',
            size: file.size,
            // Store the real File so FormData on web submits a binary blob
            file: file,
          };
        });
        resolve(uploads);
        // Cleanup
        input.remove();
      };
      input.click();
    });
  };

  const handleRemoveFile = useCallback((fileId: string) => {
    if (Platform.OS === 'web') {
      setPendingRemoveId(fileId);
      setShowConfirm(true);
      return;
    }

    // Native platforms: use cross-platform confirmation
    showConfirmWithButtons(
      'Remove File',
      'Are you sure you want to remove this file?',
      'Remove',
      'Cancel',
      () => {
        const fileToRemove = files.find(f => f.id === fileId);
        if (fileToRemove && fileToRemove.uri && createdUrlsRef.current.has(fileToRemove.uri)) {
          // eslint-disable-next-line no-empty -- intentional: revokeObjectURL best-effort cleanup
          try { (globalThis as any)?.URL?.revokeObjectURL?.(fileToRemove.uri); } catch {}
          createdUrlsRef.current.delete(fileToRemove.uri);
        }
        onFilesChange(files.filter(file => file.id !== fileId));
      },
      () => {}
    );
  }, [files, onFilesChange]);

  const getFileIcon = (type: string) => {
    if (!type) {
      return SVG_COMPONENTS.PAPERCLIP;
    }
    if (type.startsWith('image/')) {
      return SVG_COMPONENTS.FOLDER; // Use available icon
    } else if (type.includes('pdf')) {
      return SVG_COMPONENTS.FOLDER; // Use available icon
    } else if (type.includes('document') || type.includes('word')) {
      return SVG_COMPONENTS.FOLDER; // Use available icon
    } else if (type.includes('spreadsheet') || type.includes('excel')) {
      return SVG_COMPONENTS.FOLDER; // Use available icon
    } else {
      return SVG_COMPONENTS.PAPERCLIP;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <FormSection title={title} subtitle={subtitle} themeColors={themeColors ? { surface: themeColors.surface, titleText: themeColors.textPrimary, subtitleText: themeColors.textSecondary } : undefined}>
      <View style={styles.container}>
        <TouchableOpacity
          style={[styles.addButton, { backgroundColor: c.surface, borderColor: c.primary }]}
          onPress={handleAddFiles}
          disabled={files.length >= maxFiles}
        >
          <SVG_COMPONENTS.PAPERCLIP
            width={20}
            height={20}
            color={files.length >= maxFiles ? c.textTertiary : c.primary}
          />
          <Text style={[
            styles.addButtonText,
            { color: c.primary },
            files.length >= maxFiles && { color: c.textTertiary },
          ]}>
            {files.length >= maxFiles ? `Maximum ${maxFiles} files` : 'Add Files'}
          </Text>
        </TouchableOpacity>

        {files.length > 0 && (
          <View style={styles.filesList}>
            <Text style={[styles.filesHeader, { color: c.textPrimary }]}>
              {files.length} file{files.length !== 1 ? 's' : ''} selected
              {maxFiles > 1 && ` (${files.length}/${maxFiles})`}
            </Text>

            {files.map((file) => {
              const FileIcon = getFileIcon(file.type);

              return (
                <View key={file.id} style={[styles.fileItem, { backgroundColor: c.surface, borderColor: c.border }]}>
                  <View style={styles.fileInfo}>
                    <View style={[styles.fileIconContainer, { backgroundColor: c.iconBg }]}>
                      <FileIcon
                        width={16}
                        height={16}
                        color={c.textSecondary}
                      />
                    </View>
                    <View style={styles.fileDetails}>
                      <Text style={[styles.fileName, { color: c.textPrimary }]} numberOfLines={1}>
                        {file.name}
                      </Text>
                      <Text style={[styles.fileSize, { color: c.textSecondary }]}>
                        {formatFileSize(file.size)}
                      </Text>
                    </View>
                  </View>

                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => handleRemoveFile(file.id)}
                  >
                    <Text style={[styles.removeButtonText, { color: c.error }]}>x</Text>
                  </TouchableOpacity>
                </View>
              );
            })}
          </View>
        )}

        {error && (
          <Text style={[styles.errorText, { color: c.error }]}>{error}</Text>
        )}
      </View>

      {/* Desktop web confirm modal */}
      {Platform.OS === 'web' && (
        <WebConfirmModal
          visible={showConfirm}
          title={'Remove File'}
          message={'Are you sure you want to remove this file?'}
          confirmText={'Remove'}
          cancelText={'Cancel'}
          onConfirm={() => {
            if (pendingRemoveId) {
              const fileToRemove = files.find(f => f.id === pendingRemoveId);
              if (fileToRemove && fileToRemove.uri && createdUrlsRef.current.has(fileToRemove.uri)) {
                // eslint-disable-next-line no-empty -- intentional: revokeObjectURL best-effort cleanup
                try { (globalThis as any)?.URL?.revokeObjectURL?.(fileToRemove.uri); } catch {}
                createdUrlsRef.current.delete(fileToRemove.uri);
              }
              onFilesChange(files.filter(f => f.id !== pendingRemoveId));
            }
            setPendingRemoveId(null);
            setShowConfirm(false);
          }}
          onCancel={() => {
            setPendingRemoveId(null);
            setShowConfirm(false);
          }}
        />
      )}
    </FormSection>
  );
});

FileUploadSection.displayName = 'FileUploadSection';

const styles = StyleSheet.create({
  container: {},
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: 'dashed',
    padding: 16,
    minHeight: 60,
  },
  addButtonText: {
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 8,
  },
  filesList: {
    marginTop: 16,
  },
  filesHeader: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 12,
  },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  fileIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  fileDetails: {
    flex: 1,
  },
  fileName: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
  fileSize: {
    fontSize: 12,
  },
  removeButton: {
    padding: 4,
  },
  removeButtonText: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 20,
    textAlign: 'center',
    minWidth: 20,
  },
  errorText: {
    fontSize: 14,
    marginTop: 8,
  },
});
